import { IonIcon, IonItem } from "@ionic/react";
import { useTranslation } from "react-i18next";
import { checkmarkCircleOutline, locationOutline } from "ionicons/icons";
import { ProjectImage } from "@/components/molecules/project/project-image";
import { type ProjectEmployeeDayCard } from "@/lib/backend/api";
import { Routes } from "@/lib/router";
import { dayKeyToDate, formatInTz, formatTimeRange } from "@/utils/format-datetime";

/**
 * One opportunity, on one day, as a row.
 *
 * The card this replaces was a poster: a wide image, the project name, and the functions behind a
 * popover — about 140px for a choice you make by comparing it with the next one. The same rule
 * Afgerond got applies here for the opposite reason: this is a list you scan, so it should fit
 * eight options on a screen, not two.
 *
 * Everything on it was already in the response and thrown away — the times, the city, how many
 * places are left. None of it needed an api change; it needed room.
 */
export function DiscoverRow({
  card,
  onSelect,
  showDay = false,
  note,
}: {
  card: ProjectEmployeeDayCard;
  /**
   * Handle the tap here instead of pushing the project screen.
   *
   * Ontdek passes this so a row opens the signup sheet and the reader keeps their place, their
   * filters and the rows they were comparing against. Without it the row behaves as it always did,
   * which is what "Voor jou" still wants.
   */
  onSelect?: (card: ProjectEmployeeDayCard) => void;
  /**
   * Print the day on the row.
   *
   * Off in the list, where the day is the heading above the group and repeating it on every row
   * would be noise. On wherever rows are lifted out of that order — "Voor jou" pulls five of them
   * out of three months and hands them over undated, which left four rows of the same project
   * looking like four copies of one thing.
   */
  showDay?: boolean;
  /**
   * One line under the row, inside its frame.
   *
   * "Voor jou" says on every row why it is there. That used to be a paragraph the section stuck on
   * afterwards with a negative top margin, which landed it on the row's own bottom border.
   */
  note?: string;
}) {
  const { t } = useTranslation();

  const functions = card.availableFunctions ?? [];
  const slots = card.availableTimeSlots ?? [];
  const places = card.totalAvailableCount ?? 0;

  // The first slot, plus a count. Showing four ranges on one row is unreadable, and the exact
  // spread is a question for the shift itself.
  //
  // In projecttijd: een dagkaart draagt geen eigen zone, dus valt dit terug op Amsterdam. Tempo's
  // `format()` las hier de klok van het toestel, waardoor 15:00-23:00 in Toronto 09:00-17:00 werd.
  const timeLabel = slots.length
    ? `${formatTimeRange(slots[0].startAt, slots[0].endAt)}${
        slots.length > 1 ? ` +${slots.length - 1}` : ""
      }`
    : null;

  // `dayKeyToDate` in plaats van de middag-truc: `new Date("2026-08-15")` is middernacht UTC en
  // print westelijk van Greenwich als de 14e, en 12:00 lokaal loste dat alleen op zolang het
  // toestel niet ver genoeg oostwaarts stond.
  const dayLabel = showDay
    ? formatInTz(dayKeyToDate(card.day.split("T")[0]), {
        weekday: "short",
        day: "numeric",
        month: "short",
      })
    : null;

  return (
    <IonItem
      button
      detail={false}
      onClick={onSelect ? () => onSelect(card) : undefined}
      routerLink={
        onSelect
          ? undefined
          : Routes.ProjectOverview(
              { id: card.projectId },
              { search: { fromUrl: Routes.Projects(), day: card.day.split("T")[0] } },
            )
      }
      routerDirection="forward"
      lines="none"
      className="ion-no-padding w-full [--border-width:0] [--inner-padding-end:0] [--padding-end:0] [--padding-start:0]"
    >
      <div
        className={`flex w-full items-start gap-3 border-b border-solid border-bordercolor-primary py-3 ${
          card.employeeHasApplied ? "opacity-60" : ""
        }`}
      >
        {/* Ook dit duimpje staat op 16:9 (app#785). Vierkant sneed er links en
            rechts een strook af — onzichtbaar op 44px, maar het is precies de
            aanname die het contract overeind houdt: één verhouding, overal. */}
        <ProjectImage
          projectId={card.projectId}
          imageKey={card.projectImage}
          alt=""
          className="aspect-video w-16 shrink-0 rounded-lg"
        />

        <div className="flex min-w-0 flex-1 flex-col gap-0.5">
          <div className="flex items-center gap-2">
            <h3 className="min-w-0 flex-1 truncate text-md font-semibold leading-[1.3] text-txt-primary">
              {card.projectName}
            </h3>
            {/* A day you already applied for is not hidden. `canApply` is `!employeeHasApplied`
                and it is per *day*, so filtering these out took away the other shifts that day
                too — you applied for the bar and lost sight of the kitchen. */}
            {card.employeeHasApplied && (
              <span className="inline-flex shrink-0 items-center gap-1 rounded-full bg-success-10 px-2 py-0.5 text-xsm font-semibold text-success-120">
                <IonIcon icon={checkmarkCircleOutline} className="size-3" aria-hidden="true" />
                {t("common:discover.row.applied")}
              </span>
            )}
          </div>

          {functions.length > 0 && (
            <p className="truncate text-sm leading-[1.4] text-txt-secondary">
              {functions.join(" · ")}
            </p>
          )}

          <p className="flex flex-wrap items-center gap-x-2 text-13 leading-[1.4] text-txt-tertiary">
            {dayLabel && (
              <span className="font-semibold capitalize text-txt-secondary">{dayLabel}</span>
            )}
            {timeLabel && <span className="tabular-nums">{timeLabel}</span>}
            {card.city && (
              <span className="inline-flex items-center gap-0.5">
                <IonIcon icon={locationOutline} className="size-3" aria-hidden="true" />
                {card.city}
              </span>
            )}
            {places > 0 && <span>{t("common:discover.row.places", { count: places })}</span>}
          </p>

          {note && <p className="text-xsm font-medium text-txt-tertiary">{note}</p>}
        </div>
      </div>
    </IonItem>
  );
}
